import { useCallback, useEffect, useRef } from 'react'
import type { FC } from 'react'

export type AuthenticatorResult = {
  key: string
  authCode: string
  authedBy: string
}

export type AuthenticatorInput = {
  isMobileNumber: boolean
  phoneNumber: string
  email: string
  opened: boolean
  onAuth: (result: AuthenticatorResult) => void
  onClose: () => void
}

export const Authenticator: FC<AuthenticatorInput> =
  import.meta.env.VITE_MODE === 'ppg' ? PpgAuthenticator : IkyuAuthenticator

function IkyuAuthenticator({
  isMobileNumber,
  phoneNumber,
  email,
  opened,
  onAuth,
  onClose,
}: AuthenticatorInput) {
  const ref = useAuthenticator({ onAuth, onClose })
  return (
    /* @ts-ignore */
    <ikyu-authenticator
      ref={ref}
      open={opened}
      is-mobile-number={isMobileNumber}
      phone-number={phoneNumber}
      mail-address={email}
      booking-only={true}
    />
  )
}

function PpgAuthenticator({
  isMobileNumber,
  phoneNumber,
  email,
  opened,
  onAuth,
  onClose,
}: AuthenticatorInput) {
  const ref = useAuthenticator({ onAuth, onClose })
  return (
    /* @ts-ignore */
    <ppg-authenticator
      ref={ref}
      open={opened}
      is-mobile-number={isMobileNumber}
      phone-number={phoneNumber}
      mail-address={email}
      booking-only={true}
    />
  )
}

// ikyu-authenticator と ppg-authenticator の interface は共通なので、このロジックは一休/PPGで共通利用してよい
function useAuthenticator({ onAuth, onClose }: Pick<AuthenticatorInput, 'onAuth' | 'onClose'>) {
  const ref = useRef<HTMLElement>(null)
  const handlerAfterAuth = useCallback(
    (result: CustomEventInit<AuthenticatorResult[]>) => {
      if (typeof result.detail !== 'undefined') {
        onAuth(result.detail[0])
      }
    },
    [onAuth]
  )

  useEffect(() => {
    const a = ref.current
    if (a === null) {
      return
    }
    a.addEventListener('authenticated', handlerAfterAuth)
    a.addEventListener('close', onClose)
    return () => {
      a.removeEventListener('authenticated', handlerAfterAuth)
      a.removeEventListener('close', onClose)
    }
  }, [handlerAfterAuth, onClose])

  return ref
}
